import '@store/store'

import { RegistrationControllerApiFactory } from '@api-client/api'
import { Loading } from '@components'
import { useWithAmpli } from '@contexts/AmpliContextProvider'
import { useAsyncApi } from '@hooks/useAsyncApi'
import Mutex from '@services/asyncMutex'
import authService from '@services/authService'
import axios from 'axios'
import { createContext, useCallback, useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
type UserInfo = {
	id?: string
}

interface AuthContextValues {
	logout: () => void
	user: UserInfo
}

const noOp = () => void 0

const defaultAuthContextValues: AuthContextValues = {
	logout: noOp,
	user: {},
}

export const AuthContext = createContext<AuthContextValues>(
	defaultAuthContextValues
)
const mutex = new Mutex()
const refreshMutex = new Mutex()

const AuthContextProvider = () => {
	const [user, setUser] = useState<UserInfo>({})
	const [isAuthenticated, setIsAuthenticated] = useState(
		authService.isAuthenticated()
	)
	const withAmpli = useWithAmpli()
	const navigate = useNavigate()

	const registrationControllerApi = useAsyncApi(
		RegistrationControllerApiFactory
	)

	const refreshToken = useCallback(async () => {
		const token = authService.getRefreshToken()
		const release = await refreshMutex.acquire()
		try {
			if (authService.isAuthenticated()) {
				return authService.getAccessToken()
			}

			if (token) {
				authService.clearAccessToken()
				const refreshTokenResponse = await registrationControllerApi(api =>
					api.refreshToken({ refreshToken: token })
				)
				if (refreshTokenResponse?.data) {
					authService.storeToken(refreshTokenResponse.data)
				}
				setIsAuthenticated(authService.isAuthenticated())
				return refreshTokenResponse?.data?.access_token
			}
		} finally {
			release()
		}
	}, [registrationControllerApi])

	const logoutAndReturnToHome = useCallback(() => {
		authService.logout()
		navigate('/login')
	}, [navigate])

	useEffect(() => {
		if (!isAuthenticated) {
			logoutAndReturnToHome()
		}
	}, [isAuthenticated, logoutAndReturnToHome])

	useEffect(() => {
		try {
			const updateUserWithIdToken = () => {
				const idToken = authService.getIdTokenClaims()
				setUser({ id: idToken?.sub })
			}
			updateUserWithIdToken()

			const requestInterceptor = axios.interceptors.request.use(
				async config => {
					const release = await mutex.acquire()

					const token = authService.getAccessToken()

					if (config.headers && token) {
						config.headers['Authorization'] = `Bearer ${token}`
					}

					withAmpli(ampli => {
						const deviceId = ampli.client.getDeviceId()
						const sessionId = ampli.client.getSessionId()
						if (deviceId && config.headers && sessionId) {
							config.headers['X-Device-ID'] = deviceId
							config.headers['X-Session-ID'] = sessionId
						}
					})

					release()

					return config
				}
			)

			const responseInterceptor = axios.interceptors.response.use(
				response => response,
				error => {
					const originalRequest = error.config
					const isAuthError =
						error.response.status === 401 || error.response.status === 403
					if (isAuthError && !originalRequest._retry) {
						originalRequest._retry = true
						return refreshToken().then(accessToken => {
							if (accessToken) {
								originalRequest.headers.Authorization = `Bearer ${accessToken}`
							}
							return axios(originalRequest)
						})
					}

					if (isAuthError) {
						logoutAndReturnToHome()
					}
					return Promise.reject(error)
				}
			)

			return () => {
				axios.interceptors.request.eject(requestInterceptor)
				axios.interceptors.response.eject(responseInterceptor)
			}
		} catch {}
	}, [logoutAndReturnToHome, withAmpli, navigate, refreshToken])

	if (!isAuthenticated) {
		return <Loading noWrapper={false} containerHeight="100vh" />
	}

	return (
		<AuthContext.Provider
			value={{ logout: isAuthenticated ? logoutAndReturnToHome : noOp, user }}
		>
			<Outlet />
		</AuthContext.Provider>
	)
}

export default AuthContextProvider
